import { useState, useCallback, useEffect, useMemo } from 'react';
import { Building2, ChevronLeft, Plus, FolderOpen, Clock, Lock, Unlock, Trash2 } from 'lucide-react';
import WeekGrid from './WeekGrid';
import WeekGenerate from './WeekGenerate';
import './Urenlijst.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
}

// The three company profiles the urenlijst can be issued under.
const PROFILES = [
  { key: 'ozel',   name: 'Özel Uitzendbureau',   sub: 'Schuttevaerweg 81' },
  { key: 'myform', name: 'Myform BV',            sub: 'Schuttevaerweg 79' },
  { key: 'entice', name: 'Entice Uitzendbureau', sub: 'Schuttevaerweg 83' },
];

function getISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return { week: Math.ceil(((d - yearStart) / 86400000 + 1) / 7), year: d.getUTCFullYear() };
}

const fmtHours = (n) => (Number(n) || 0).toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');

export default function Urenlijst() {
  const iso = getISOWeek(new Date());

  const [profiel, setProfiel] = useState(null);
  const [weken, setWeken] = useState([]);   // lijsten: meerdere per week mogelijk
  const [loadingWeken, setLoadingWeken] = useState(false);
  const [sel, setSel] = useState(null); // de geopende lijst { id, jaar, week, ... }
  const [klanten, setKlanten] = useState([]); // uit het Klanten-menu (clients)

  useEffect(() => {
    authFetch('/api/clients')
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setKlanten(Array.isArray(d) ? d : []))
      .catch(() => setKlanten([]));
  }, []);
  const [newYear, setNewYear] = useState(iso.year);

  // Weekvergrendeling: week blijft zichtbaar, maar wijzigen kan pas na ontgrendelen.
  const [lockBusy, setLockBusy] = useState(false);
  const [lockVersion, setLockVersion] = useState(0);
  const selLijst = sel ? weken.find((w) => w.id === sel.id) || sel : null;
  const selLocked = !!selLijst?.locked;

  const toggleLock = async () => {
    if (!sel || lockBusy) return;
    const wantLock = !selLocked;
    const code = window.prompt(
      wantLock
        ? `Week ${sel.week} vergrendelen. Voer de code in:`
        : `Week ${sel.week} ontgrendelen. Voer de code in:`
    );
    if (code === null) return;
    setLockBusy(true);
    try {
      const res = await authFetch(`/api/uren/week/${wantLock ? 'lock' : 'unlock'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lijst_id: sel.id, code }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { window.alert(d.error || 'Mislukt'); return; }
      await loadWeken(profiel);
      setLockVersion((v) => v + 1); // forceer het raster opnieuw te laden
    } catch {
      window.alert('Mislukt — probeer opnieuw.');
    } finally {
      setLockBusy(false);
    }
  };

  const [newWeek, setNewWeek] = useState(iso.week);

  const profileMeta = PROFILES.find((p) => p.key === profiel);

  const loadWeken = useCallback((key) => {
    setLoadingWeken(true);
    return authFetch(`/api/uren/lijsten?profiel=${encodeURIComponent(key)}`)
      .then((r) => r.json())
      .then((d) => { const list = Array.isArray(d) ? d : []; setWeken(list); return list; })
      .catch(() => { setWeken([]); return []; })
      .finally(() => setLoadingWeken(false));
  }, []);

  const openProfiel = (key) => {
    setProfiel(key);
    setSel(null);
    loadWeken(key).then((list) => {
      if (list.length) setSel(list[0]);
    });
  };

  const backToProfielen = () => { setProfiel(null); setWeken([]); setSel(null); };

  // Week openen maakt een nieuwe lijst. Bestaat die week al, dan eerst vragen:
  // wie alleen naar week 37 wil springen, krijgt zo geen dubbele lijst.
  const openWeek = async (jaar, week) => {
    jaar = Number(jaar); week = Number(week);
    const bestaand = weken.filter((w) => w.jaar === jaar && w.week === week);
    if (bestaand.length) {
      const namen = bestaand.map((w) => w.klant_naam || 'geen klant').join(', ');
      const nog = window.confirm(
        `Week ${week} (${jaar}) bestaat al (${namen}).\n\n` +
        `OK = nog een week ${week} openen, bijvoorbeeld voor een andere klant.\n` +
        `Annuleren = de bestaande week ${week} openen.`
      );
      if (!nog) { setSel(bestaand[0]); return; }
    }
    try {
      const res = await authFetch('/api/uren/lijsten', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ profiel, jaar, week }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { window.alert(d.error || 'Week openen mislukt.'); return; }
      setWeken((prev) => [...prev, d].sort((a, b) => b.jaar - a.jaar || b.week - a.week || a.id - b.id));
      setSel(d);
    } catch {
      window.alert('Week openen mislukt — probeer opnieuw.');
    }
  };

  const selectWeek = (lijst) => {
    // Tellingen verversen bij het wisselen, daarna selecteren.
    if (profiel) loadWeken(profiel);
    setSel(lijst);
  };

  // Klant kiezen voor de geopende lijst.
  const kiesKlant = async (value) => {
    if (!sel) return;
    const id = value ? Number(value) : null;
    const naam = klanten.find((k) => k.id === id)?.name || null;
    try {
      const res = await authFetch(`/api/uren/lijsten/${sel.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ klant_id: id }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); window.alert(d.error || 'Opslaan mislukt.'); return; }
      setWeken((prev) => prev.map((w) => (w.id === sel.id ? { ...w, klant_id: id, klant_naam: naam } : w)));
      setSel((prev) => (prev ? { ...prev, klant_id: id, klant_naam: naam } : prev));
    } catch { window.alert('Opslaan mislukt — probeer opnieuw.'); }
  };

  // Alleen een lege lijst kan weg (de server controleert dat ook).
  const verwijderLijst = async (lijst) => {
    if (!window.confirm(`Lege week ${lijst.week} (${lijst.jaar})${lijst.klant_naam ? ` — ${lijst.klant_naam}` : ''} verwijderen?`)) return;
    try {
      const res = await authFetch(`/api/uren/lijsten/${lijst.id}`, { method: 'DELETE' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { window.alert(d.error || 'Verwijderen mislukt.'); return; }
      setWeken((prev) => prev.filter((w) => w.id !== lijst.id));
      if (sel?.id === lijst.id) setSel(null);
    } catch { window.alert('Verwijderen mislukt — probeer opnieuw.'); }
  };

  // Hoeveel lijsten per week — bij meer dan één tonen we ook 'Geen klant'.
  const perWeek = useMemo(() => {
    const m = {};
    for (const w of weken) m[`${w.jaar}-${w.week}`] = (m[`${w.jaar}-${w.week}`] || 0) + 1;
    return m;
  }, [weken]);
  const gridParams = useMemo(() => (sel ? { lijst_id: sel.id } : null), [sel?.id]);

  // ── Profiel selectie ─────────────────────────────────────────────────────────
  if (!profiel) {
    return (
      <div className="url-root">
        <div className="url-head">
          <h2 className="url-title">Urenlijst</h2>
          <p className="url-sub">Kies een bedrijf om de weekurenlijsten te beheren</p>
        </div>
        <div className="url-profiel-grid">
          {PROFILES.map((p) => (
            <button key={p.key} className={`url-profiel-card ${p.key}`} onClick={() => openProfiel(p.key)}>
              <span className="url-profiel-icon"><Building2 size={26} /></span>
              <span className="url-profiel-name">{p.name}</span>
              <span className="url-profiel-sub">{p.sub}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Werkblad: weken-mappen links, grid rechts ────────────────────────────────
  return (
    <div className="url-root">
      <div className="url-crumb">
        <button className="url-back" onClick={backToProfielen}><ChevronLeft size={16} /> Bedrijven</button>
        <span className="url-crumb-sep">/</span>
        <span className="url-crumb-current">{profileMeta?.name}</span>
      </div>

      {/* Weken als dropdown bovenaan, zodat de urentabel de volle breedte heeft */}
      <div className="url-weekbar">
        <label className="url-weekpick">
          <span>Week</span>
          <select value={sel?.id || ''} disabled={loadingWeken}
            onChange={(e) => { const w = weken.find((x) => String(x.id) === e.target.value); if (w) selectWeek(w); }}>
            {!sel && <option value="">{loadingWeken ? 'Laden…' : weken.length ? 'Kies een week…' : 'Nog geen weken'}</option>}
            {weken.map((w) => {
              const dubbel = perWeek[`${w.jaar}-${w.week}`] > 1;
              const klant = w.klant_naam || (dubbel ? 'Geen klant' : '');
              return (
                <option key={w.id} value={w.id}>
                  {`Week ${w.week} · ${w.jaar}${klant ? ` · ${klant}` : ''} · ${w.aantalPersonen} pers · ${fmtHours(w.totaalUren)}u${w.locked ? ' 🔒' : ''}`}
                </option>
              );
            })}
          </select>
        </label>
        {selLijst && selLijst.aantalPersonen === 0 && !selLijst.locked && (
          <button type="button" className="url-folder-del url-weekbar-del" title="Lege lijst verwijderen"
            onClick={() => verwijderLijst(selLijst)}>
            <Trash2 size={14} />
          </button>
        )}
        <div className="url-weekbar-new">
          <label>Week
            <input type="number" min="1" max="53" value={newWeek}
              onChange={(e) => setNewWeek(Math.max(1, Math.min(53, parseInt(e.target.value) || 1)))} />
          </label>
          <label>Jaar
            <input type="number" min="2020" max="2099" value={newYear}
              onChange={(e) => setNewYear(parseInt(e.target.value) || iso.year)} />
          </label>
          <button className="url-btn-primary" onClick={() => openWeek(newYear, newWeek)}>
            <Plus size={15} /> Week openen
          </button>
        </div>
      </div>

      <div className="url-workspace is-collapsed">
        <div className="url-weekmain">
          {sel ? (
            <>
              <div className="url-weekmain-head">
                <div className="url-weekmain-title">
                  <Clock size={16} /><span>Week {sel.week} — {sel.jaar}</span>
                  <select className="url-og-select" value={selLijst?.klant_id || ''}
                    onChange={(e) => kiesKlant(e.target.value)} disabled={selLocked}
                    title={selLocked ? 'Week is vergrendeld' : 'Klant van deze lijst'}>
                    <option value="">Geen klant</option>
                    {klanten.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                  </select>
                  <button type="button" className={`url-lockbtn ${selLocked ? 'is-locked' : ''}`}
                    onClick={toggleLock} disabled={lockBusy}
                    title={selLocked ? 'Week is vergrendeld — klik om te ontgrendelen' : 'Week vergrendelen met code'}>
                    {selLocked ? <Lock size={14} /> : <Unlock size={14} />}
                    {selLocked ? 'Vergrendeld' : 'Vergrendelen'}
                  </button>
                </div>
                <WeekGenerate key={`gen-${sel.id}`} profiel={profiel} jaar={sel.jaar} week={sel.week}
                  lijstId={sel.id} label={selLijst?.klant_naam || ''} />
              </div>
              <WeekGrid key={`${sel.id}-${lockVersion}`} profiel={profiel} jaar={sel.jaar} week={sel.week}
                extraParams={gridParams} />
            </>
          ) : (
            <div className="url-weekmain-empty">
              <FolderOpen size={40} strokeWidth={1.2} />
              <p>Kies een week bovenaan, of open een nieuwe week.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
